import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog, IconButton, TextField, Typography } from '@mui/material';
import RuleIcon from '@mui/icons-material/Rule';
import DosenTooltip from '../../../../../../../../components/ui/DosenTooltip';
import CloseIcon from '@mui/icons-material/Close';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { toast } from 'react-toastify';
import { API } from '../../../../../../../../api';
import { useAppSelector } from '../../../../../../../../redux/hooks';
import { useProgramContext } from '../../../../../../../../hooks/useProgramContext';
import useProgramCreationFlowV2 from '../../../../../../../../hooks/useProgramCreationFlowV2';
import useEditingFrozen from '../../../../../../../../hooks/useEditingFrozen';
import { EDITING_FROZEN_MESSAGE } from '../../../../../../../../utils/constants';
import useLessonVideoBuilder from '../../../../../../../../hooks/useLessonVideoBuilder';
import { lessonVideoJobExpired } from '../../../../../../../../utils/lessonVideoJobDeadline';
import LessonVideoCard from '../../../../../../../../components/video-builder/LessonVideoCard';
import ImproveWithAIModal from '../../../../../aimd-v2/components/ImproveWithAIModal';
import {
  Lesson,
  ModuleDraft,
  createLesson,
  createModule
} from '../../../../../../../../components/admin3/newProgram/masterclass/types';
import { ModuleStructure, MCQ } from '../../../../../aimd-v2/types';
import ModuleSidebar from '../../../../../aimd-v2/components/ModuleSidebar';
import LessonContentView, { ElliottSectionAdapter, type RoleplayPolicy } from '../../../../../aimd-v2/components/LessonContentView';
import LessonTagLine from '../../../../../aimd-v2/components/LessonTagLine';
import LessonPurposeDialog, { type LessonPurposeOption } from '../../../../../aimd-v2/components/LessonPurposeDialog';
import CapabilityConfirm, {
  toastWithUndo,
  type CapabilityConfirmRequest
} from '../../../../../aimd-v2/components/CapabilityConfirm';
import { lessonCapability, savedRoleplayFlags, type LessonTagChoice } from '../../../../../aimd-v2/lessonTags';
import {
  capabilitiesLeftWithoutLesson,
  capabilityLostByRetag,
  lastLessonWarning,
  lastScoredRoleplayOf,
  lessonsOfModule,
  otherScoredRoleplayLesson,
  scoredRoleplayCounts
} from '../../../../../aimd-v2/capabilityGuards';
import EditLessonModal from '../../../../../aimd-v2/components/EditLessonModal';
import { useAimdHeartbeat } from '../../../../../aimd-v2/hooks/useAimdHeartbeat';
import DueDateModal from '../../settings components/Dialog/DueDateModal';
import { mapTemplateToModules } from '../utils';
import {
  buildTemplateLevelsPayload,
  isObjectId,
  lessonNeedsDraft,
  lessonToGeneratedLesson,
  mcqsToMultipleChoice
} from '../designMappers';
import FeedbackQuestionsSection from './FeedbackQuestionsSection';
import LessonResourcesSection from './LessonResourcesSection';
import DosenButton from '../../../../../../../../components/ui/Buttons/DosenButton';
import { LoadingSpinner } from './LoadingSpinner';
import { useCourseVersioning } from '../versioning/CourseVersioningContext';
import { VersionPreview } from '../versioning/VersionPreview';
import RequirementsHost from '../requirements/RequirementsHost';
import { ReqModule, readAssessRoles, withDummyCapabilities } from '../requirements/requirements';
import { openRequirements, useAudienceGroups, useRequirementsPilot } from '../requirements/useRequirements';
import { audienceRequirementsBackend } from '../requirements/flags';

/** The modules with one lesson's Scored Roleplay flag set (docs/PRD-capability-lesson-roleplays.md R9). */
const withLessonRoleplay = (modules: ModuleDraft[], mIdx: number, lIdx: number, scored: boolean): ModuleDraft[] =>
  modules.map((m, mi) =>
    mi !== mIdx ? m : { ...m, lessons: m.lessons.map((l, li) => (li === lIdx ? { ...l, roleplay: scored } : l)) }
  );

/** The modules with no lesson marked to resend its Elliott sections (after that save went through). */
const withoutResendFlags = (modules: ModuleDraft[]): ModuleDraft[] =>
  modules.map((m) => ({ ...m, lessons: m.lessons.map(({ resendElliott, ...l }) => l) }));

/** A learning outcome as an option of the lesson-purpose picker. */
const outcomeOption = (outcome: string): LessonPurposeOption => ({ choice: { outcome }, label: outcome });

interface DesignBuilderProps {
  templateId: string;
  templateGoalName?: string;
  grpId: string;
  designTemplate: any;
  registerSaveHandler: (handler: (() => Promise<boolean>) | null) => void;
  onDirtyChange: (dirty: boolean) => void;
  onProgramNameChange?: (value: string) => void;
}

/**
 * Post-creation "Program Design" editor, presented as the Elliott stage-6 builder
 * (module sidebar + rich lesson editor) but running against the LIVE template for
 * both AI-built and manually-built programs. Structural edits + lesson content /
 * quiz / feedback save via updateGroupTasks; the Elliott coaching sections
 * (Case Studies · Live Scenario · Quiz AI) persist immediately through the
 * task-scoped elliott-section endpoints (see the section adapter below).
 *
 * With course versioning on (org flag, CourseVersioningProvider mounted) the
 * editor state is seeded from the program DRAFT instead of the live template,
 * every edit autosaves to the draft (the provider debounces and flushes), the
 * task-scoped endpoints write to the draft server-side and the editor refreshes
 * the touched lesson from the draft afterwards. Save Changes gives way to
 * Publish update; the legacy path below stays for the flag-off case.
 */
export function DesignBuilder({
  templateId,
  templateGoalName,
  grpId,
  designTemplate,
  registerSaveHandler,
  onDirtyChange
}: DesignBuilderProps) {
  // ATC: count active admin time in the design editor (BE stops once "Invited").
  useAimdHeartbeat(grpId);

  const { orgId: ctxOrgId, location } = useProgramContext();
  const orgMongoId = useAppSelector((s) => s.orgDetails.data?._id) || '';
  const orgAuthId = useAppSelector((s) => s.orgDetails.data?.authId) || '';
  // Elliott task endpoints match users.oid against the org's Auth0 authId.
  const elliottOrgAuthId = (ctxOrgId as string) || orgAuthId;
  // updateGroupTasks (verifyOrganization) matches the Mongo _id, like the manual builder.
  const saveOrgId = orgMongoId || (ctxOrgId as string);

  // Course versioning (null when off): the editor runs on the draft.
  const versioning = useCourseVersioning();
  const versioningOn = Boolean(versioning);

  const [modules, setModules] = useState<ModuleDraft[]>(() =>
    versioning?.draftModules ? versioning.draftModules : mapTemplateToModules(designTemplate)
  );
  const [selModuleId, setSelModuleId] = useState<string | null>(null);
  const [selLessonId, setSelLessonId] = useState<string | null>(null);
  const [editLessonOpen, setEditLessonOpen] = useState(false);
  const [moduleDueDateTargetId, setModuleDueDateTargetId] = useState<string | null>(null);

  const dirtyRef = useRef(false);
  const hydrationBlockedRef = useRef(false);
  const didMountRef = useRef(false);

  const markDirty = useCallback(() => {
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      onDirtyChange(true);
    }
  }, [onDirtyChange]);

  const setModulesDirty = useCallback(
    (updater: (prev: ModuleDraft[]) => ModuleDraft[]) => {
      setModules((prev) => updater(prev));
      markDirty();
    },
    [markDirty]
  );

  // Elliott-section changes are already persisted server-side — update local state
  // WITHOUT marking the builder dirty (mirrors ManualProgramBuilder.syncModulesWithoutDirty).
  const syncModules = useCallback((updater: (prev: ModuleDraft[]) => ModuleDraft[]) => {
    setModules((prev) => updater(prev));
  }, []);

  // Re-hydrate when the parent refetches the template (unless the user has
  // unsaved edits). A template belonging to a DIFFERENT templateId always
  // re-hydrates, dirty or not: edits made against the wrong program's
  // template must not pin it on screen. With versioning on the draft is the
  // source of truth: a template refetch never touches the editor.
  const seededTemplateIdRef = useRef(templateId);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (versioningOn) return;
    const templateChanged = seededTemplateIdRef.current !== templateId;
    if (!templateChanged && (hydrationBlockedRef.current || dirtyRef.current)) return;
    seededTemplateIdRef.current = templateId;
    if (templateChanged && dirtyRef.current) {
      dirtyRef.current = false;
      onDirtyChange(false);
    }
    setModules(mapTemplateToModules(designTemplate));
    setSelModuleId(null);
  }, [designTemplate, templateId, onDirtyChange, versioningOn]);

  // Default the selection to the first lesson once modules exist.
  useEffect(() => {
    if (selModuleId === null && modules.length > 0) {
      setSelModuleId(modules[0].id);
      setSelLessonId(modules[0].lessons[0]?.id ?? null);
    }
  }, [modules, selModuleId]);

  // Course versioning: report every editor state to the provider (it
  // autosaves the draft), and accept the provider writing back — a full
  // replacement when the draft is (re)loaded or a version restored (selection
  // kept by template id where the lesson still exists), and functional
  // updates for the server ids adopted after a save. Neither marks the
  // builder dirty: the draft already holds what the server wrote.
  const draftLoaded = Boolean(versioning?.draftLoaded);
  const reportDraft = versioning?.reportDraft;
  const registerRestoreHandler = versioning?.registerRestoreHandler;
  const registerModulesUpdater = versioning?.registerModulesUpdater;
  const draftModules = versioning?.draftModules ?? null;
  const appliedDraftRef = useRef<ModuleDraft[] | null>(null);
  // Latest modules / selection for async callbacks and the snapshot apply.
  const modulesRef = useRef(modules);
  modulesRef.current = modules;
  const selectionRef = useRef({ moduleId: selModuleId, lessonId: selLessonId });
  selectionRef.current = { moduleId: selModuleId, lessonId: selLessonId };
  const applySnapshot = useCallback((snapshot: ModuleDraft[]) => {
    appliedDraftRef.current = snapshot;
    const prevModule = modulesRef.current.find((m) => m.id === selectionRef.current.moduleId);
    const prevLesson = prevModule?.lessons.find((l) => l.id === selectionRef.current.lessonId);
    const nextModule =
      (prevModule?.sourceId && snapshot.find((m) => m.sourceId === prevModule.sourceId)) || snapshot[0] || null;
    const nextLesson =
      (prevLesson?.sourceId && nextModule?.lessons.find((l) => l.sourceId === prevLesson.sourceId)) ||
      nextModule?.lessons[0] ||
      null;
    setModules(snapshot);
    setSelModuleId(nextModule?.id ?? null);
    setSelLessonId(nextLesson?.id ?? null);
  }, []);
  useEffect(() => {
    if (!draftModules || appliedDraftRef.current === draftModules) return;
    if (appliedDraftRef.current === null && modules === draftModules) {
      // Seeded from the draft at mount.
      appliedDraftRef.current = draftModules;
      return;
    }
    applySnapshot(draftModules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftModules, applySnapshot]);
  useEffect(() => {
    // Only editor states derived from the draft are reported: the first one
    // after a (re)load is the provider's autosave baseline.
    if (reportDraft && draftLoaded && appliedDraftRef.current) reportDraft(modules);
  }, [reportDraft, draftLoaded, modules]);
  useEffect(() => {
    if (!registerRestoreHandler) return undefined;
    registerRestoreHandler(applySnapshot);
    return () => registerRestoreHandler(null);
  }, [registerRestoreHandler, applySnapshot]);
  useEffect(() => {
    if (!registerModulesUpdater) return undefined;
    registerModulesUpdater((fn) => setModules((prev) => fn(prev)));
    return () => registerModulesUpdater(null);
  }, [registerModulesUpdater]);

  const activeModuleIdx = modules.findIndex((m) => m.id === selModuleId);
  const activeModule = activeModuleIdx >= 0 ? modules[activeModuleIdx] : null;
  const activeLessonIdx = activeModule ? activeModule.lessons.findIndex((l) => l.id === selLessonId) : -1;
  const activeLesson = activeModule && activeLessonIdx >= 0 ? activeModule.lessons[activeLessonIdx] : null;

  /* ── §4 (creation-flow revamp) — lesson-level AI CTAs, at parity with the
     Build-with-AI editor: Improve with AI (task-scoped 'content' improve),
     AI Reasoning (disabled until reasoning data exists on the task), and
     Generate Lesson Video (group-scoped endpoint, per-program quota). */
  const creationFlowV2 = useProgramCreationFlowV2();
  // Relaunch editing freeze (B4-2): the surface is wrapped read-only upstream;
  // these guards make sure nothing hits the network even if a click gets through.
  const editingFrozen = useEditingFrozen();
  // Lesson Video Builder (Presenter + Explainer) replaces the HeyGen CTA when its flag is on.
  const videoBuilder = useLessonVideoBuilder();
  // Audience requirements (prototype: The Playground, development builds). With the backend on, the grid
  // reads the backend (RequirementsHost); DUMMY until then: groups made up here, and the editor's lessons.
  const requirementsOn = useRequirementsPilot();
  const requirementsDummy = requirementsOn && !audienceRequirementsBackend();
  const audience = useAudienceGroups(grpId, requirementsDummy);
  // Modules and lessons by their saved ids, as the requirements read them.
  const reqModules = useMemo<ReqModule[]>(
    () =>
      requirementsDummy
        ? withDummyCapabilities(
            modules.map((m) => ({
              id: String(m.sourceId || m.id),
              title: m.title,
              lessons: m.lessons.map((l) => ({
                id: String(l.sourceId || l.id),
                title: l.title,
                capabilityName: l.capabilityName ?? null
              }))
            })),
            readAssessRoles(audience.groups)
          )
        : [],
    [requirementsDummy, modules, audience.groups]
  );
  const groupDetailsData = useAppSelector((s: any) => s.groupDetails?.data);
  const [improveLessonOpen, setImproveLessonOpen] = useState(false);
  const [improveSaving, setImproveSaving] = useState(false);
  const [improvingLesson, setImprovingLesson] = useState<'draft' | 'improve' | null>(null);
  const [enrolledWarnOpen, setEnrolledWarnOpen] = useState(false);
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [videoPromptOpen, setVideoPromptOpen] = useState(false);
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoJobs, setVideoJobs] = useState<
    { videoJobId: string; moduleId: string; lessonId: string; startedAt: number }[]
  >([]);
  const [videoCount, setVideoCount] = useState(0);

  const activeLessonTaskReady =
    Boolean(activeModule && activeLesson) &&
    isObjectId(activeModule?.sourceId) &&
    isObjectId(activeLesson?.sourceId);

  // Lightweight stopgap warning before overwriting content learners already
  // see. With course versioning on the rewrite lands in the draft (the BE
  // snapshots a `before_ai` version first) and learners see nothing until
  // Publish update, so the warning is skipped.
  const handleImproveClick = () => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    const enrolledCount = Array.isArray(groupDetailsData?.mentees) ? groupDetailsData.mentees.length : 0;
    if (enrolledCount > 0 && !versioningOn) setEnrolledWarnOpen(true);
    else setImproveLessonOpen(true);
  };

  // A lesson with no real content yet is drafted from its title + the notes
  // rather than rewritten (the BE makes the same call; this only picks the copy).
  const activeLessonNeedsDraft = lessonNeedsDraft(activeLesson?.description);
  // What the modal's "saved first" note is about: the draft's pending autosave
  // with versioning on, the legacy Save Changes dirty state otherwise.
  const unsavedEditsPending = versioning
    ? versioning.saveState === 'pending' || versioning.saveState === 'saving' || versioning.saveState === 'error'
    : dirtyRef.current;

  // Versioning: put the editor state (with any new title) in the draft and
  // return the server ids of the lesson at [mIdx][lIdx]. The provider adopts
  // minted ids into the editor after its save, but that lands on a later
  // render, so the draft is read back here. A save already in flight (the
  // debounced autosave) is waited out with a few short retries.
  const saveDraftForImprove = async (
    toSave: ModuleDraft[],
    changed: boolean,
    mIdx: number,
    lIdx: number
  ): Promise<{ levelId: string; taskId: string } | null> => {
    if (!versioning) return null;
    if (changed) {
      setModulesDirty(() => toSave);
      versioning.reportDraft(toSave);
    }
    const wantedModule = toSave[mIdx];
    const wantedLesson = wantedModule?.lessons[lIdx];
    let flushed = false;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      flushed = await versioning.flushDraft();
      const fresh = flushed ? await versioning.fetchDraftModules() : null;
      const m = fresh?.[mIdx];
      const l = m?.lessons[lIdx];
      const sameModule = !wantedModule?.sourceId || m?.sourceId === wantedModule.sourceId;
      const sameLesson = !wantedLesson?.sourceId || l?.sourceId === wantedLesson.sourceId;
      if (m && l && sameModule && sameLesson && isObjectId(m.sourceId) && isObjectId(l.sourceId)) {
        return { levelId: m.sourceId as string, taskId: l.sourceId as string };
      }
      if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 600));
    }
    toast.error(
      flushed
        ? "Your draft is saved, but this lesson didn't load back. Refresh the page and try again."
        : "Your draft couldn't be saved. Please try again."
    );
    return null;
  };

  // Returns false when nothing went through, so the modal keeps the admin's notes.
  const handleImproveLessonSubmit = async (feedback: string, extra?: { newTitle?: string }): Promise<boolean> => {
    if (!activeModule || !activeLesson) return false;
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return false;
    }
    const mIdx = activeModuleIdx;
    const lIdx = activeLessonIdx;
    const newTitle = extra?.newTitle?.trim();
    const toSave =
      newTitle && newTitle !== activeLesson.title
        ? modules.map((m, i) =>
            i !== mIdx ? m : { ...m, lessons: m.lessons.map((l, j) => (j === lIdx ? { ...l, title: newTitle } : l)) }
          )
        : modules;

    // The improve endpoint works on the saved task, so a lesson added since
    // the last save (or a new title) has to reach the server first. With
    // versioning on "the server" is the DRAFT, through the provider's autosave:
    // the legacy save below would write the live template and refetch it into
    // the editor, and the next autosave would copy that over the draft.
    let localModuleId = activeModule.id;
    let localLessonId = activeLesson.id;
    let levelId = activeModule.sourceId;
    let taskId = activeLesson.sourceId;
    let targetDescription = activeLesson.description;
    if (versioning) {
      setImproveSaving(true);
      const ids = await saveDraftForImprove(toSave, toSave !== modules, mIdx, lIdx);
      setImproveSaving(false);
      if (!ids) return false;
      levelId = ids.levelId;
      taskId = ids.taskId;
    } else if (toSave !== modules || !activeLessonTaskReady || dirtyRef.current) {
      // Legacy Save Changes path. dirtyRef covers edits elsewhere in the
      // program: the modal says they are saved first, so they are.
      setImproveSaving(true);
      const saved = await saveModules(toSave);
      setImproveSaving(false);
      if (!saved) return false;
      const m = saved[mIdx];
      const l = m?.lessons[lIdx];
      if (!m || !l || !isObjectId(m.sourceId) || !isObjectId(l.sourceId)) {
        toast.error("Your changes are saved, but this lesson didn't load back. Refresh the page and try again.");
        return false;
      }
      localModuleId = m.id;
      localLessonId = l.id;
      levelId = m.sourceId;
      taskId = l.sourceId;
      targetDescription = l.description;
    }
    if (!isObjectId(levelId) || !isObjectId(taskId)) {
      toast.error('Save the program first, then improve this lesson.');
      return false;
    }

    const drafting = lessonNeedsDraft(targetDescription);
    setImproveLessonOpen(false);
    setImprovingLesson(drafting ? 'draft' : 'improve');
    try {
      const res: any = await API.improveElliottSectionOnTask({
        grpId,
        levelId: levelId as string,
        taskId: taskId as string,
        orgId: elliottOrgAuthId,
        section: 'content',
        feedback
      });
      const data = res?.data?.data ?? res?.data ?? {};
      if (!data.content) throw new Error('No content returned');
      // Persisted server-side (content + reasoning on the task) — sync local
      // state without marking the builder dirty. Versioning: the write went
      // to the draft, so the lesson is refreshed from there.
      const fresh = versioningOn ? await refetchLesson(levelId, taskId) : null;
      updateLessonById(localModuleId, localLessonId, (l) => ({
        ...l,
        description: fresh?.description || data.content,
        reasoning: fresh?.reasoning || data.reasoning || l.reasoning,
        elliott: fresh ? fresh.elliott : l.elliott
      }));
      toast.success(
        data.drafted
          ? 'Lesson drafted. Case studies, Roleplay and quiz are being built in the background.'
          : 'Lesson Body improved. Case studies, Roleplay and quiz are realigning in the background.'
      );
      return true;
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          (drafting
            ? "Elliott couldn't draft this lesson. Please try again."
            : 'Elliott could not improve the Lesson Body. Try again.')
      );
      // Reopen with the notes still in it, so a retry is one click.
      setImproveLessonOpen(true);
      return false;
    } finally {
      setImprovingLesson(null);
    }
  };

  const handleGenerateVideoSubmit = async () => {
    if (!activeModule || !activeLesson || !videoPrompt.trim()) return;
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    if (!activeLessonTaskReady) {
      toast.error("This lesson isn't saved yet. Click Save Changes, then generate its video.");
      return;
    }
    const prompt = videoPrompt.trim();
    const moduleId = activeModule.id;
    const lessonId = activeLesson.id;
    setVideoPromptOpen(false);
    setVideoPrompt('');
    try {
      const res: any = await API.generateTaskLessonVideo({
        grpId,
        levelId: activeModule.sourceId as string,
        taskId: activeLesson.sourceId as string,
        orgId: elliottOrgAuthId,
        prompt
      });
      const data = res?.data?.data ?? res?.data ?? {};
      if (data.videoJobId) {
        setVideoJobs((prev) => [...prev, { videoJobId: data.videoJobId, moduleId, lessonId, startedAt: Date.now() }]);
        setVideoCount((c) => c + 1);
        toast.success('Video requested. It will appear here in 5 to 7 minutes.');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Elliott could not make the video. Try again.');
    }
  };

  // ---- Structural editing (all wired to ModuleSidebar's index-based callbacks) ----
  const updateModuleAt = (idx: number, updater: (m: ModuleDraft) => ModuleDraft) =>
    setModulesDirty((prev) => prev.map((m, i) => (i === idx ? updater(m) : m)));

  // One lesson, fresh from the server: the draft with versioning on (the
  // task-scoped endpoints write there), the live template otherwise.
  const fetchDraftModules = versioning?.fetchDraftModules;
  const refetchLesson = useCallback(
    async (moduleSourceId?: string, lessonSourceId?: string): Promise<Lesson | null> => {
      if (!moduleSourceId || !lessonSourceId) return null;
      try {
        let mapped: ModuleDraft[] | null;
        if (fetchDraftModules) {
          mapped = await fetchDraftModules();
        } else {
          const res = await API.getTemplateByTemplateId({
            templateId,
            location: location || '',
            grpId,
            orgId: elliottOrgAuthId
          });
          mapped = mapTemplateToModules(res?.data?.data);
        }
        return (
          (mapped || []).find((m) => m.sourceId === moduleSourceId)?.lessons.find((l) => l.sourceId === lessonSourceId) ||
          null
        );
      } catch {
        return null;
      }
    },
    [fetchDraftModules, templateId, location, grpId, elliottOrgAuthId]
  );

  const updateLessonById = useCallback(
    (moduleId: string, lessonId: string, updater: (l: Lesson) => Lesson, dirty = false) => {
      const fn = (prev: ModuleDraft[]) =>
        prev.map((m) =>
          m.id !== moduleId ? m : { ...m, lessons: m.lessons.map((l) => (l.id === lessonId ? updater(l) : l)) }
        );
      if (dirty) setModulesDirty(fn);
      else syncModules(fn);
    },
    [setModulesDirty, syncModules]
  );

  const dropVideoJob = useCallback(
    (videoJobId: string) => setVideoJobs((prev) => prev.filter((j) => j.videoJobId !== videoJobId)),
    []
  );

  // Poll pending video jobs; a finished video is appended to the lesson content
  // (dirty → the admin's Save persists it, same contract as the wizard). With
  // versioning on the draft is refreshed first: when the BE already wrote the
  // video into the draft task the editor adopts it as saved, otherwise the
  // local append autosaves.
  useEffect(() => {
    if (!videoJobs.length) return undefined;
    const interval = setInterval(async () => {
      for (const job of videoJobs) {
        // Nothing on the server ever ends one of these jobs; without this the
        // lesson would report itself as generating a video for as long as the
        // page stayed open.
        if (lessonVideoJobExpired(job.startedAt)) {
          toast.error("Your lesson video didn't arrive in time. Try generating it again.");
          dropVideoJob(job.videoJobId);
          continue;
        }
        try {
          const res: any = await API.getTaskLessonVideoStatus({
            grpId,
            videoJobId: job.videoJobId,
            orgId: elliottOrgAuthId
          });
          const data = res?.data?.data ?? res?.data ?? {};
          if (data?.status === 'completed' && data?.url) {
            const current = modulesRef.current.find((m) => m.id === job.moduleId);
            const currentLesson = current?.lessons.find((l) => l.id === job.lessonId);
            const fresh = versioningOn ? await refetchLesson(current?.sourceId, currentLesson?.sourceId) : null;
            const alreadyInDraft = Boolean(fresh?.description && fresh.description.includes(data.url));
            updateLessonById(
              job.moduleId,
              job.lessonId,
              (l) =>
                alreadyInDraft && fresh
                  ? { ...l, description: fresh.description }
                  : {
                      ...l,
                      description:
                        (l.description || '').trimEnd() +
                        `<div style="margin:16px 0 0"><video src="${data.url}" controls style="max-width:100%;border-radius:8px;margin:12px 0;" /></div>`
                    },
              !alreadyInDraft
            );
            dropVideoJob(job.videoJobId);
            toast.success(
              versioningOn
                ? 'Your lesson video is ready. It is in your draft.'
                : 'Your lesson video is ready. Remember to save the program.'
            );
          } else if (data?.status === 'failed') {
            toast.error('Elliott could not make the video. Try again.');
            dropVideoJob(job.videoJobId);
          }
        } catch {
          /* keep polling */
        }
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [videoJobs, grpId, elliottOrgAuthId, updateLessonById, versioningOn, refetchLesson, dropVideoJob]);

  // ---- What each lesson is for (docs/PRD-capability-lesson-roleplays.md R8) ----
  // The program's capabilities and outcomes, from the draft (null for a program built another way).
  const lessonTags = versioning?.lessonTags ?? null;
  const scoredFlags = savedRoleplayFlags(modules);
  const [confirm, setConfirm] = useState<CapabilityConfirmRequest | null>(null);
  const [purpose, setPurpose] = useState<{
    title: string;
    body?: string;
    options: LessonPurposeOption[];
    initial?: LessonTagChoice | null;
    confirmLabel: string;
    resolve: (choice: LessonTagChoice | null) => void;
  } | null>(null);
  const askPurpose = (ask: Omit<NonNullable<typeof purpose>, 'resolve'>) =>
    new Promise<LessonTagChoice | null>((resolve) => {
      setPurpose({ ...ask, resolve });
    });
  const closePurpose = (choice: LessonTagChoice | null) => {
    purpose?.resolve(choice);
    setPurpose(null);
  };
  const capabilityOptions = (): LessonPurposeOption[] => {
    if (!lessonTags) return [];
    const counts = scoredRoleplayCounts(modules, scoredFlags);
    return lessonTags.capabilities.map((c) => {
      const n = counts[c.id] ?? 0;
      return { choice: { capabilityId: c.id }, label: c.name, note: `${n} Scored Roleplay${n === 1 ? '' : 's'}` };
    });
  };
  const tagFieldsOf = (choice: LessonTagChoice | null): Partial<Lesson> =>
    !choice
      ? {}
      : 'capabilityId' in choice
      ? {
          capabilityId: choice.capabilityId,
          outcomes: [],
          roleplay: false,
          capabilityName: lessonTags?.capabilities.find((c) => c.id === choice.capabilityId)?.name ?? null
        }
      : { capabilityId: null, outcomes: [choice.outcome], roleplay: false, capabilityName: null };

  /** In a program whose lessons carry tags, a lesson added here says what it is for first. */
  const askNewLessonPurpose = async (): Promise<LessonTagChoice | null | false> => {
    if (!lessonTags) return null;
    const choice = await askPurpose({
      title: 'What is this lesson for?',
      body: 'A lesson builds one capability or serves a learning outcome. Its Roleplay and quiz count toward the capability it builds.',
      options: [
        ...capabilityOptions(),
        ...lessonTags.outcomes.map(outcomeOption)
      ],
      confirmLabel: 'Add lesson'
    });
    return choice ?? false;
  };

  const handleAddModule = async () => {
    const choice = await askNewLessonPurpose();
    if (choice === false) return;
    const mod = createModule(modules.length + 1);
    const tagged = { ...mod, lessons: mod.lessons.map((l) => ({ ...l, ...tagFieldsOf(choice) })) };
    setModulesDirty((prev) => [...prev, tagged]);
    setSelModuleId(tagged.id);
    setSelLessonId(tagged.lessons[0].id);
  };

  const handleAddLesson = async (mIdx: number) => {
    const choice = await askNewLessonPurpose();
    if (choice === false) return;
    const lesson = { ...createLesson(), ...tagFieldsOf(choice) };
    setModulesDirty((prev) => prev.map((m, i) => (i === mIdx ? { ...m, lessons: [...m.lessons, lesson] } : m)));
    setSelModuleId(modules[mIdx].id);
    setSelLessonId(lesson.id);
  };

  // A delete can be undone. With versioning the draft autosaves meanwhile and no longer has the lesson's
  // Elliott sections, so a lesson put back sends them once with the next save (R5).
  const deleteWithUndo = (next: ModuleDraft[], message: string) => {
    const before = modules;
    setModulesDirty(() => next);
    toastWithUndo(message, () => {
      const kept = new Set(next.flatMap((m) => m.lessons.map((l) => l.id)));
      const restored = before.map((m) => ({
        ...m,
        lessons: m.lessons.map((l) => (kept.has(l.id) || !versioningOn ? l : { ...l, resendElliott: true }))
      }));
      setModulesDirty(() => restored);
      if (!versioning) return;
      // Saved straight away, so the sections go back once and later saves leave them to the server again.
      versioning.reportDraft(restored);
      versioning.flushDraft().then((ok) => {
        if (!ok) return;
        syncModules(withoutResendFlags);
      });
    });
  };

  const handleDeleteModule = (idx: number) => {
    const run = () => {
      const removed = modules[idx];
      const remaining = modules.filter((_, i) => i !== idx);
      deleteWithUndo(remaining, 'Module deleted.');
      if (removed.id === selModuleId) {
        const next = remaining[0] || null;
        setSelModuleId(next?.id ?? null);
        setSelLessonId(next?.lessons[0]?.id ?? null);
      }
    };
    const lost = lessonTags ? capabilitiesLeftWithoutLesson(modules, lessonTags, lessonsOfModule(modules, idx)) : [];
    if (lost.length === 0) return run();
    setConfirm({ title: 'Delete this module?', body: lastLessonWarning(lost, 'module'), confirmLabel: 'Delete anyway', onConfirm: run });
  };

  const handleDeleteLesson = (mIdx: number, lIdx: number) => {
    const run = () => {
      const mod = modules[mIdx];
      const removed = mod.lessons[lIdx];
      let nextLessons = mod.lessons.filter((_, i) => i !== lIdx);
      let replacement: Lesson | null = null;
      if (nextLessons.length === 0) {
        replacement = createLesson();
        nextLessons = [replacement];
      }
      deleteWithUndo(
        modules.map((m, i) => (i === mIdx ? { ...m, lessons: nextLessons } : m)),
        'Lesson deleted.'
      );
      if (removed.id === selLessonId) {
        setSelLessonId(replacement?.id ?? nextLessons[0]?.id ?? null);
      }
    };
    const lost = lessonTags
      ? capabilitiesLeftWithoutLesson(modules, lessonTags, [{ moduleIndex: mIdx, lessonIndex: lIdx }])
      : [];
    if (lost.length === 0) return run();
    setConfirm({ title: 'Delete this lesson?', body: lastLessonWarning(lost), confirmLabel: 'Delete anyway', onConfirm: run });
  };

  // The lesson's Roleplay rules in the editor (R9): a Roleplay measures the capability the lesson builds.
  const pickCapability = async (
    mIdx: number,
    lIdx: number,
    reason: 'switchOn' | 'change'
  ): Promise<{ id: string; name: string } | null> => {
    if (!lessonTags) return null;
    const current = modules[mIdx]?.lessons[lIdx];
    const serves = current?.outcomes?.length ? `"${current.outcomes.join('", "')}"` : 'its outcomes';
    const choice = await askPurpose({
      title: reason === 'switchOn' ? 'Which capability should this Roleplay measure?' : 'Which capability does this lesson build?',
      body:
        reason === 'switchOn'
          ? `Roleplays in this course measure a capability. This lesson will build it instead of serving ${serves}. Its content stays as it is.`
          : 'The lesson keeps its content. Its Roleplay measures the capability you choose.',
      options: capabilityOptions(),
      initial: current?.capabilityId ? { capabilityId: current.capabilityId } : null,
      confirmLabel: reason === 'switchOn' ? 'Add Roleplay' : 'Change'
    });
    if (!choice || !('capabilityId' in choice)) return null;
    const capability = lessonTags.capabilities.find((c) => c.id === choice.capabilityId) ?? null;
    if (!capability || capability.id === current?.capabilityId) return capability;
    const lost = capabilityLostByRetag(modules, lessonTags, { moduleIndex: mIdx, lessonIndex: lIdx }, choice);
    if (lost) {
      // Publish Update refuses a course that builds a capability with no lesson.
      const proceed = await new Promise<boolean>((resolve) => {
        setConfirm({
          title: 'Change what this lesson is for?',
          body: lastLessonWarning([lost]),
          confirmLabel: 'Change anyway',
          onConfirm: () => resolve(true),
          alt: { label: 'Keep it', onClick: () => resolve(false) },
          required: true
        });
      });
      if (!proceed) return null;
    }
    const next = modules.map((m, mi) =>
      mi !== mIdx
        ? m
        : {
            ...m,
            lessons: m.lessons.map((l, li) =>
              li === lIdx ? { ...l, ...tagFieldsOf(choice), roleplay: l.roleplay === true } : l
            )
          }
    );
    setModulesDirty(() => next);
    // The Roleplay toggle saves on the draft's task, which must already build the capability.
    if (versioning) {
      versioning.reportDraft(next);
      if (!(await versioning.flushDraft())) return null;
    }
    return capability;
  };

  const roleplayPolicyFor = (mIdx: number, lIdx: number): RoleplayPolicy | undefined => {
    const lesson = modules[mIdx]?.lessons[lIdx];
    if (!lessonTags || !lesson || !versioningOn) return undefined;
    const at = { moduleIndex: mIdx, lessonIndex: lIdx };
    return {
      capability: lessonCapability(lesson, lessonTags),
      outcomes: lesson.outcomes ?? [],
      lastScored: Boolean(lastScoredRoleplayOf(modules, lessonTags, scoredFlags, at)),
      stillMeasuredIn: otherScoredRoleplayLesson(modules, scoredFlags, at)?.title ?? null,
      pickCapability: (reason) => pickCapability(mIdx, lIdx, reason),
      // Already saved on the draft's task: keep the editor's copy in step without marking it dirty.
      onScoredChange: (scored) => syncModules((prev) => withLessonRoleplay(prev, mIdx, lIdx, scored))
    };
  };

  // ---- Elliott section adapter (task-scoped, for the selected lesson) ----
  const sectionAdapter: ElliottSectionAdapter | undefined = useMemo(
    () =>
      activeModule && activeLesson
        ? {
            ready: isObjectId(activeModule.sourceId) && isObjectId(activeLesson.sourceId),
            mode: 'task',
            persistSection: async (payload) => {
              if (editingFrozen) {
                toast.warn(EDITING_FROZEN_MESSAGE);
                return;
              }
              const moduleId = activeModule.id;
              const lessonId = activeLesson.id;
              const levelId = activeModule.sourceId as string;
              const taskId = activeLesson.sourceId as string;
              // Rubric typing + on/off toggles apply optimistically and ignore the
              // server echo (which would clobber keystrokes in flight); case-study
              // saves apply the returned elliott.
              const optimistic =
                Boolean(payload.simulationRubric) ||
                payload.simulationEnabled !== undefined ||
                payload.caseStudiesEnabled !== undefined ||
                payload.quizEnabled !== undefined;
              if (optimistic) {
                updateLessonById(moduleId, lessonId, (l) => ({
                  ...l,
                  elliott: {
                    ...(l.elliott || {}),
                    ...(payload.simulationRubric
                      ? {
                          simulationRubric: {
                            ...payload.simulationRubric,
                            maxAttempts: l.elliott?.simulationRubric?.maxAttempts ?? 5
                          }
                        }
                      : {}),
                    ...(payload.simulationEnabled !== undefined
                      ? { simulationEnabled: payload.simulationEnabled }
                      : {}),
                    ...(payload.caseStudiesEnabled !== undefined
                      ? { caseStudiesEnabled: payload.caseStudiesEnabled }
                      : {}),
                    ...(payload.quizEnabled !== undefined ? { quizEnabled: payload.quizEnabled } : {})
                  }
                }));
              }
              const res = await API.updateElliottSectionOnTask({
                grpId,
                levelId,
                taskId,
                orgId: elliottOrgAuthId,
                ...payload
              });
              const next = res.data?.data?.elliott ?? res.data?.elliott;
              if (next && !optimistic) updateLessonById(moduleId, lessonId, (l) => ({ ...l, elliott: next }));
            },
            improveSection: async (section, feedback, caseStudyIndex) => {
              if (editingFrozen) {
                toast.warn(EDITING_FROZEN_MESSAGE);
                return undefined;
              }
              const moduleId = activeModule.id;
              const lessonId = activeLesson.id;
              const levelId = activeModule.sourceId as string;
              const taskId = activeLesson.sourceId as string;
              const res = await API.improveElliottSectionOnTask({
                grpId,
                levelId,
                taskId,
                orgId: elliottOrgAuthId,
                section,
                feedback,
                caseStudyIndex
              });
              const next = res.data?.data?.elliott ?? res.data?.elliott;
              if (section === 'quiz') {
                // The BE replaced task.MCQ (encrypted) — refetch this lesson and swap
                // in the fresh questions so the accordion + save stay in sync.
                const fresh = await refetchLesson(levelId, taskId);
                if (fresh) {
                  updateLessonById(moduleId, lessonId, (l) => ({
                    ...l,
                    multipleChoiceQuestions: fresh.multipleChoiceQuestions,
                    elliott: fresh.elliott
                  }));
                  const freshMcqs: MCQ[] = lessonToGeneratedLesson(fresh).mcqs ?? [];
                  return { mcqs: freshMcqs, elliott: next };
                }
                return { elliott: next };
              }
              if (next) updateLessonById(moduleId, lessonId, (l) => ({ ...l, elliott: next }));
              return { elliott: next };
            }
          }
        : undefined,
    [activeModule, activeLesson, grpId, elliottOrgAuthId, updateLessonById, refetchLesson]
  );

  const genLesson = useMemo(() => (activeLesson ? lessonToGeneratedLesson(activeLesson) : null), [activeLesson]);

  // ---- Save (structural + content + quiz + feedback via the template) ----
  // Saves `toSave` and returns the refetched modules (newly added lessons now
  // carry real sourceIds), or null when the save failed. Improve with AI calls
  // it directly to save a lesson that only exists in this editor.
  const saveModules = async (toSave: ModuleDraft[]): Promise<ModuleDraft[] | null> => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return null;
    }
    if (!templateId) {
      toast.error('No template to save');
      return null;
    }
    if (toSave.length === 0) {
      toast.error('Add at least one module before saving');
      return null;
    }
    hydrationBlockedRef.current = true;
    try {
      const goalName = (templateGoalName?.trim() || 'Program').replace(/[^a-zA-Z0-9\s-_]/g, '') || 'Program';
      const payload = { goalName, programType: 'Custom', levels: buildTemplateLevelsPayload(toSave) };
      const res = await API.updateGroupTasks({
        data: payload,
        grpId,
        location: location || '',
        orgId: saveOrgId,
        templateId
      });
      const status = (res as any)?.status;
      if (!res || (status && status >= 400) || !(res as any)?.data) {
        toast.error('Your changes could not be saved. Your session may have expired. Refresh the page and try again.');
        return null;
      }
      // No success toast here: the page-level save confirms once for the whole save.

      // Refetch so newly-added modules/lessons gain real sourceIds (enables Elliott AI).
      const selM = toSave.findIndex((m) => m.id === selModuleId);
      const selL = selM >= 0 ? toSave[selM].lessons.findIndex((l) => l.id === selLessonId) : -1;
      let saved = toSave;
      try {
        const fresh = await API.getTemplateByTemplateId({
          templateId,
          location: location || '',
          grpId,
          orgId: elliottOrgAuthId
        });
        const mapped = mapTemplateToModules(fresh?.data?.data);
        saved = mapped;
        setModules(mapped);
        const m = selM >= 0 ? mapped[selM] : mapped[0];
        setSelModuleId(m?.id ?? null);
        setSelLessonId((selL >= 0 ? m?.lessons[selL]?.id : undefined) ?? m?.lessons[0]?.id ?? null);
      } catch {
        /* keep local state if the refetch fails */
        setModules(toSave);
      }

      dirtyRef.current = false;
      onDirtyChange(false);
      return saved;
    } catch {
      toast.error('The program could not be saved. Try again.');
      return null;
    } finally {
      hydrationBlockedRef.current = false;
    }
  };

  const performSave = async (): Promise<boolean> => (await saveModules(modules)) !== null;

  const performSaveRef = useRef(performSave);
  performSaveRef.current = performSave;
  useEffect(() => {
    registerSaveHandler(() => performSaveRef.current());
    return () => registerSaveHandler(null);
  }, [registerSaveHandler]);

  const sidebarModules = modules as unknown as ModuleStructure[];

  // Versioning: the editor waits for the draft (the template alone would show
  // the live course and let edits race the draft load).
  if (versioning && versioning.draftError) {
    return (
      <Typography fontSize={12} color="#F16063">
        {versioning.draftError}
      </Typography>
    );
  }
  if (versioning && !versioning.draftLoaded) return <LoadingSpinner minHeight={320} message="Loading your draft" />;

  // Previewing an older version replaces the editor; this component (and its
  // modules state) stays mounted, so Resume current restores it untouched.
  if (versioning?.previewEntry) return <VersionPreview draft={modules} />;

  return (
    <Box sx={{ width: '100%', display: 'flex', gap: 3, minHeight: 0, flex: 1 }}>
      {/* Left — module/lesson tree (stage-6 sidebar, structural editing on) */}
      <Box
        sx={{
          width: 320,
          flexShrink: 0,
          overflowY: 'auto',
          borderRight: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          pr: 2
        }}
      >
        <ModuleSidebar
          programName={templateGoalName || ''}
          hideProgramName
          showReasoning={false}
          readOnly={false}
          modules={sidebarModules}
          selectedModuleIdx={activeModuleIdx >= 0 ? activeModuleIdx : null}
          selectedLessonIdx={activeLessonIdx >= 0 ? activeLessonIdx : null}
          onSelectModule={(idx) => {
            const m = modules[idx];
            setSelModuleId(m.id);
            setSelLessonId(m.lessons[0]?.id ?? null);
          }}
          onSelectLesson={(mIdx, lIdx) => {
            const m = modules[mIdx];
            setSelModuleId(m.id);
            setSelLessonId(m.lessons[lIdx].id);
          }}
          onReorder={(updated) => setModulesDirty(() => updated as unknown as ModuleDraft[])}
          onRenameModule={(idx, title) => updateModuleAt(idx, (m) => ({ ...m, title }))}
          onRenameLesson={(mIdx, lIdx, title) =>
            updateModuleAt(mIdx, (m) => ({
              ...m,
              lessons: m.lessons.map((l, i) => (i === lIdx ? { ...l, title } : l))
            }))
          }
          onAddModule={() => {
            handleAddModule();
          }}
          onAddLesson={(mIdx) => {
            handleAddLesson(mIdx);
          }}
          onDeleteModule={handleDeleteModule}
          onDeleteLesson={handleDeleteLesson}
          onReasoningClick={() => {}}
          onModuleDueDate={(idx) => setModuleDueDateTargetId(modules[idx].id)}
          renderLessonExtra={
            lessonTags
              ? (mIdx, lIdx) =>
                  modules[mIdx]?.lessons[lIdx] ? (
                    // What the lesson is for; it changes from the lesson's Roleplay section (Change).
                    <LessonTagLine
                      lesson={modules[mIdx].lessons[lIdx]}
                      roleplay={Boolean(scoredFlags[mIdx]?.[lIdx])}
                      tags={lessonTags}
                      editable={false}
                      onTag={() => undefined}
                    />
                  ) : null
              : undefined
          }
        />
      </Box>

      {requirementsOn && (
        <RequirementsHost
          grpId={grpId}
          lessonTitle={activeLesson?.title}
          dummy={requirementsDummy ? { modules: reqModules, groups: audience.groups, loading: audience.loading } : null}
        />
      )}

      {/* Right — lesson editor (Content · Case Studies · Live Scenario · Quiz · Feedback) */}
      <Box sx={{ flex: 1, overflowY: 'auto', pt: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {activeLesson && genLesson ? (
          <>
            <LessonContentView
              key={activeLesson.id}
              roleplayPolicy={roleplayPolicyFor(activeModuleIdx, activeLessonIdx)}
              lesson={genLesson}
              capabilityName={activeLesson.capabilityName ?? null}
              moduleName={activeModule?.title || ''}
              moduleIndex={activeModuleIdx}
              lessonIndex={activeLessonIdx}
              sectionAdapter={sectionAdapter}
              onEdit={() => setEditLessonOpen(true)}
              {...(creationFlowV2
                ? {
                    onImprove: handleImproveClick,
                    onReasoningClick: () => setReasoningOpen(true),
                    reasoningDisabled: !activeLesson.reasoning,
                    reasoningDisabledTooltip:
                      'No AI reasoning yet for this lesson — run Improve with AI to generate it.'
                  }
                : {})}
              {...(creationFlowV2 && !videoBuilder
                ? {
                    onGenerateVideo: () => {
                      setVideoPrompt('');
                      setVideoPromptOpen(true);
                    },
                    videoCount,
                    isGeneratingVideo: videoJobs.some((j) => j.lessonId === activeLesson.id)
                  }
                : {})}
              videoSlot={
                videoBuilder && activeLessonTaskReady ? (
                  <LessonVideoCard
                    programId={grpId}
                    moduleId={activeModule!.sourceId as string}
                    lessonId={activeLesson.sourceId as string}
                    lessonName={activeLesson.title}
                    moduleName={activeModule?.title || ''}
                  />
                ) : undefined
              }
              titleExtra={
                requirementsOn ? (
                  <DosenTooltip title="Audience Requirements for this lesson">
                    <IconButton
                      size="small"
                      aria-label="Audience Requirements for this lesson"
                      data-testid="requirements-lesson"
                      onClick={() =>
                        openRequirements({
                          kind: 'lesson',
                          moduleId: String(activeModule!.sourceId || activeModule!.id),
                          lessonId: String(activeLesson.sourceId || activeLesson.id)
                        })
                      }
                      sx={{ color: 'var(--Stroke-highlight, #F79420)', p: 0.5 }}
                    >
                      <RuleIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </DosenTooltip>
                ) : undefined
              }
              onAssessmentsUpdate={(mcqs) =>
                updateLessonById(
                  activeModule!.id,
                  activeLesson.id,
                  (l) => ({ ...l, multipleChoiceQuestions: mcqsToMultipleChoice(mcqs, l.multipleChoiceQuestions) }),
                  true
                )
              }
              resourcesSlot={
                <LessonResourcesSection
                  grpId={grpId}
                  orgId={(ctxOrgId as string) || ''}
                  location={location || ''}
                  materials={activeLesson.materials}
                  onChange={(mats) =>
                    updateLessonById(activeModule!.id, activeLesson.id, (l) => ({ ...l, materials: mats }), true)
                  }
                />
              }
            />
            <FeedbackQuestionsSection
              questions={activeLesson.feedbackQuestions}
              onChange={(qs) =>
                updateLessonById(activeModule!.id, activeLesson.id, (l) => ({ ...l, feedbackQuestions: qs }), true)
              }
            />
          </>
        ) : activeModule ? (
          <Box sx={{ maxWidth: 560 }}>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                fontFamily: 'Inter',
                color: 'var(--Text-text-primary, #FDFDFD)',
                mb: 1
              }}
            >
              {activeModule.title}
            </Typography>
            {activeModule.objective && (
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-secondary, #B6B6B6)',
                  lineHeight: '22px',
                  mb: 2
                }}
              >
                {activeModule.objective}
              </Typography>
            )}
            <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
              {activeModule.lessons.length} lesson{activeModule.lessons.length === 1 ? '' : 's'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxWidth: 560 }}>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 600,
                fontFamily: 'Inter',
                color: 'var(--Text-text-primary, #FDFDFD)',
                mb: 2
              }}
            >
              Design
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: 'Inter',
                color: 'var(--Text-text-secondary, #B6B6B6)',
                lineHeight: '22px'
              }}
            >
              Add a module from the sidebar, then select a lesson to edit its Lesson Body, case studies, Roleplay, quiz
              and Survey questions.
            </Typography>
          </Box>
        )}
      </Box>

      {activeLesson && (
        <EditLessonModal
          open={editLessonOpen}
          onClose={() => setEditLessonOpen(false)}
          onSave={(title, content) => {
            updateLessonById(activeModule!.id, activeLesson.id, (l) => ({ ...l, title, description: content }), true);
            setEditLessonOpen(false);
          }}
          lessonTitle={activeLesson.title}
          lessonContent={activeLesson.description}
        />
      )}

      {/* §4 — Improve with AI (lesson content, task-scoped) */}
      <ImproveWithAIModal
        variant="lesson"
        open={improveLessonOpen}
        onClose={() => setImproveLessonOpen(false)}
        onSubmit={handleImproveLessonSubmit}
        isLoading={improveSaving}
        loadingText="Saving..."
        entityName={activeLesson?.title}
        bannerText={[
          activeLessonNeedsDraft
            ? 'Elliott writes this lesson from its title and your notes, then builds its case studies, Roleplay and quiz to match.'
            : "Elliott rewrites this lesson's content from your notes, then rebuilds its case studies, Roleplay and quiz to match.",
          unsavedEditsPending || !activeLessonTaskReady
            ? versioning
              ? 'Your latest edits are saved to your draft first.'
              : 'Your unsaved changes to modules and lessons are saved first.'
            : ''
        ]
          .filter(Boolean)
          .join(' ')}
      />

      {/* §4 — enrolled-learners stopgap warning before overwriting live content */}
      <Dialog
        open={enrolledWarnOpen}
        onClose={() => setEnrolledWarnOpen(false)}
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 4,
            maxWidth: 440,
            width: '100%'
          }
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', mb: 2 }}>
          This program has enrolled learners
        </Typography>
        <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '22px', mb: 3 }}>
          Updating this lesson will change it for them. Continue?
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <DosenButton tone="basic" fullWidth onClick={() => setEnrolledWarnOpen(false)}>
            Cancel
          </DosenButton>
          <DosenButton
            fullWidth
            onClick={() => {
              setEnrolledWarnOpen(false);
              setImproveLessonOpen(true);
            }}
          >
            Continue
          </DosenButton>
        </Box>
      </Dialog>

      {/* §4 — "Making changes" while the content improve runs */}
      <Dialog
        open={improvingLesson !== null}
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3,
            minWidth: 360
          }
        }}
      >
        <Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', mb: 2 }}>
          Making changes
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HourglassBottomIcon
            sx={{
              fontSize: 18,
              color: 'var(--Text-text-secondary, #B6B6B6)',
              animation: 'hourglass-spin 2s ease-in-out infinite',
              '@keyframes hourglass-spin': {
                '0%, 40%': { transform: 'rotate(0deg)' },
                '60%, 100%': { transform: 'rotate(180deg)' }
              }
            }}
          />
          <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}>
            {improvingLesson === 'draft'
              ? 'Elliott is drafting this lesson from your notes...'
              : 'Elliott is improving the Lesson Body...'}
          </Typography>
        </Box>
      </Dialog>

      {/* §4 — AI Reasoning modal (same layout as the wizard's) */}
      <Dialog
        open={reasoningOpen && !!activeLesson}
        onClose={() => setReasoningOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3,
            maxHeight: '80vh'
          }
        }}
      >
        {activeLesson && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Stroke-highlight, #F79420)' }}>
                    Elliott's reasoning
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 22, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
                  {activeLesson.title}
                </Typography>
              </Box>
              <Box
                component="button"
                onClick={() => setReasoningOpen(false)}
                sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5, flexShrink: 0 }}
              >
                <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 24 }} />
              </Box>
            </Box>
            {activeLesson.reasoning ? (
              <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', lineHeight: '24px' }}>
                {activeLesson.reasoning}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}>
                No reasoning from Elliott yet. Run Improve with Elliott to generate it.
              </Typography>
            )}
            {(activeLesson.learningOutcomesServed?.length ?? 0) > 0 && (
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', mb: 0.5 }}>
                  Goals served
                </Typography>
                {activeLesson.learningOutcomesServed!.map((o, i) => (
                  <Typography key={i} sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}>
                    • {o}
                  </Typography>
                ))}
              </Box>
            )}
            {(activeLesson.ragDocumentTitles?.length ?? 0) > 0 && (
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', mb: 0.5 }}>
                  Grounded in your documents
                </Typography>
                {activeLesson.ragDocumentTitles!.map((t, i) => (
                  <Typography key={i} sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)' }}>
                    • {t}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Dialog>

      {/* §4 — Generate Lesson Video prompt */}
      <Dialog
        open={videoPromptOpen}
        onClose={() => setVideoPromptOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1A1A1A',
            borderRadius: '12px',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            p: 3
          }
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', mb: 1 }}>
          Generate a Lesson video
        </Typography>
        <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 2 }}>
          Describe what the presenter should focus on. The script is grounded in the Lesson Body.
        </Typography>
        <TextField
          value={videoPrompt}
          onChange={(e) => setVideoPrompt(e.target.value)}
          placeholder="e.g. A quick, energetic recap of the three key takeaways"
          multiline
          rows={3}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#0F0F0F',
              color: '#FDFDFD',
              fontFamily: 'Inter',
              fontSize: 14,
              '& fieldset': { borderColor: '#2A2A2A' },
              '&:hover fieldset': { borderColor: '#404040' },
              '&.Mui-focused fieldset': { borderColor: 'var(--Stroke-highlight, #F79420)' }
            }
          }}
        />
        <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
          <DosenButton tone="basic" fullWidth onClick={() => setVideoPromptOpen(false)}>
            Cancel
          </DosenButton>
          <DosenButton fullWidth onClick={handleGenerateVideoSubmit} disabled={!videoPrompt.trim()}>
            Generate
          </DosenButton>
        </Box>
      </Dialog>

      <CapabilityConfirm request={confirm} onClose={() => setConfirm(null)} />
      <LessonPurposeDialog
        open={Boolean(purpose)}
        title={purpose?.title ?? ''}
        body={purpose?.body}
        options={purpose?.options ?? []}
        initial={purpose?.initial}
        confirmLabel={purpose?.confirmLabel ?? ''}
        onCancel={() => closePurpose(null)}
        onConfirm={(choice) => closePurpose(choice)}
      />

      <DueDateModal
        open={Boolean(moduleDueDateTargetId)}
        onClose={() => setModuleDueDateTargetId(null)}
        mode="module"
        initialData={
          moduleDueDateTargetId
            ? (() => {
                const m = modules.find((mod) => mod.id === moduleDueDateTargetId);
                return m ? { startDate: m.startDate, endDate: m.endDate, description: m.objective } : undefined;
              })()
            : undefined
        }
        onSave={(data) => {
          if (!moduleDueDateTargetId) return;
          const idx = modules.findIndex((m) => m.id === moduleDueDateTargetId);
          if (idx >= 0) updateModuleAt(idx, (m) => ({ ...m, startDate: data.startDate, endDate: data.endDate }));
          setModuleDueDateTargetId(null);
        }}
      />
    </Box>
  );
}

export default DesignBuilder;
